/**
 * ErrorState Component
 *
 * Generic error state display with retry functionality.
 * Used when data fetching fails.
 *
 * @module domains/personnel/presentation/components/shared/ErrorState
 */

import { useTranslation } from 'react-i18next';
import { Button } from '@parrot-software/design-system';

/**
 * Props for ErrorState component
 */
export interface ErrorStateProps {
  context?: 'roles' | 'employees';
  onRetry?: () => void;
  error?: string | Error | null;
}

const styles = {
  wrapper: 'flex items-center justify-center py-12',
  content: 'text-center flex flex-col items-center gap-3',
  title: 'text-sm text-destructive font-medium',
  description: 'text-xs text-muted-foreground',
};

/**
 * Extracts error message from various error types
 */
const getErrorMessage = (error: string | Error | null | undefined): string | null => {
  if (!error) return null;
  if (typeof error === 'string') return error;
  if (error instanceof Error) return error.message;
  return null;
};

/**
 * ErrorState Component
 *
 * Displays an error message with optional retry button.
 * Supports context-specific messages for roles and employees.
 * Can display custom error messages from backend.
 *
 * @param {ErrorStateProps} props - Component properties
 * @returns {React.ReactElement} Error state component
 *
 * @example
 * // With context-specific message
 * <ErrorState context="roles" onRetry={() => refetch()} />
 *
 * @example
 * // With backend error message
 * <ErrorState context="roles" error={apiError} onRetry={() => refetch()} />
 */
export const ErrorState = ({ context, onRetry, error }: ErrorStateProps): React.ReactElement => {
  const { t } = useTranslation('personnel');

  const title = context ? t(`errorState.${context}.title`) : t('errorState.title');
  const defaultDescription = context ? t(`errorState.${context}.description`) : t('errorState.description');
  const errorMessage = getErrorMessage(error);
  const description = errorMessage || defaultDescription;

  return (
    <div className={styles.wrapper}>
      <div className={styles.content}>
        <p className={styles.title}>{title}</p>
        <p className={styles.description}>{description}</p>
        {onRetry && (
          <Button onClick={onRetry} variant="outline">
            {t('errorState.retry')}
          </Button>
        )}
      </div>
    </div>
  );
};
